/*
 * Copyright 2024, Corning Optical Communications
 * All Rights Reserved.
 */

import React, { useEffect, useMemo, useState } from "react";
import { Grid, Typography, Button } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid-pro";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../../components/ui/dialog";
import { Icon } from "../../../components/Shared/Icon";
import { Spinner } from "../../../components/ui/spinner";

import CustomDataGrid from "../../../components/Shared/Table/CustomDataGrid";
import StatusIcon from "../../../components/Shared/Status/StatusIcon";
import { useGetDasRadioNodesQuery } from "../../../store/api/devices/deviceApiSlice";
import { IRemoteEquipment } from "../../../store/api/devices/types";
import { useStoreDispatch } from "../../../store/hooks";
import { setConfigCounts } from "../../../store/api/deviceConfig/reducers";
import { CONFIGUREROUTES } from "../../../globals/globals";
import { resolveDeviceStatusLabel } from "../../../utils/deviceStatus";
import DasDeviceDetailsDrawer from "../../../components/Drawers/DasDeviceDetailsDrawer";
import { selectedDeviceSelector } from "../../../store/api/devices/selectors";
import DasInventoryFilter from "../../../components/Configuration/DasInventoryFilter";
import { searchKeywordSelector } from "../../../store/search/selector";
import { resetKeyword } from "../../../store/search/reducer";
import { LOCAL_STORAGE } from "../../../globals/globals";
import { setOpenToast } from "../../../store/helpers/helperReducers";
import {
  dasInventoryEquipmentTypeSelector,
  dasInventoryStatusSelector,
  dasInventoryBandSelector,
} from "../../../store/filters/dasInventory/selectors";
import { resetDasInventoryFilters } from "../../../store/filters/dasInventory/reducer";
import { capitalizeWords } from "../../../utils/helpers";

const DasRadioNodes: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const locationPath = location?.pathname.split("/");
  const deviceKey = locationPath?.[2];
  const dispatch = useStoreDispatch();
  const selectedDeviceDetail = useSelector(selectedDeviceSelector);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState<IRemoteEquipment | null>(null);

  // Get filter values from Redux
  const selectedEquipmentType = useSelector(dasInventoryEquipmentTypeSelector);
  const selectedStatus = useSelector(dasInventoryStatusSelector);
  const selectedBand = useSelector(dasInventoryBandSelector);
  const searchKeyword = useSelector(searchKeywordSelector);

  const { data = [], isLoading, refetch } = useGetDasRadioNodesQuery(
    { deviceKey }, 
    { 
      skip: !deviceKey,
      pollingInterval: 30000,
      refetchOnMountOrArgChange: 30, // Use cache if data is less than 30 seconds old
    }
  );

  // Filter the data based on selected filters and search
  const filteredData = useMemo(() => {
    let filtered = data;

    // Apply equipment type filter
    if (selectedEquipmentType) {
      filtered = filtered.filter((item) => item.equipmentType === selectedEquipmentType);
    }

    // Apply status filter
    if (selectedStatus) {
      filtered = filtered.filter((item) => {
        const statusLabel = resolveDeviceStatusLabel(item.status);
        return statusLabel === selectedStatus;
      });
    }

    // Apply band filter
    if (selectedBand) {
      filtered = filtered.filter((item) => item.band === selectedBand);
    }

    // Apply search filter (search in hostname and serialNumber)
    if (searchKeyword && searchKeyword.length >= 3) {
      const keyword = searchKeyword.toLowerCase();
      filtered = filtered.filter((item) => {
        const hostname = (item.hostname || "").toLowerCase();
        const serialNumber = (item.serialNumber || "").toLowerCase();
        return hostname.includes(keyword) || serialNumber.includes(keyword);
      });
    }

    return filtered;
  }, [data, selectedEquipmentType, selectedStatus, selectedBand, searchKeyword]);

  useEffect(() => {
    dispatch(setConfigCounts({ [CONFIGUREROUTES.DASRADIONODES.Path]: filteredData.length }));
  }, [dispatch, filteredData.length]);

  // Reset filters when component unmounts (navigating away)
  useEffect(() => {
    return () => {
      dispatch(resetDasInventoryFilters());
      dispatch(resetKeyword());
    };
  }, [dispatch]);

  // Update selectedDevice with fresh data when drawer is open and data refreshes
  useEffect(() => {
    if (drawerOpen && selectedDevice && data.length > 0) {
      const updatedDevice = data.find((device) => device.objectKey === selectedDevice.objectKey);
      if (updatedDevice) {
        setSelectedDevice(updatedDevice);
      }
    }
  }, [data, drawerOpen, selectedDevice?.objectKey]);

  const handleRowClick = (device: IRemoteEquipment) => {
    setSelectedDevice(device);
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
  };

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: "hostname",
        headerName: "Name",
        width: 240,
        valueGetter: (params) => params.row?.hostname || "",
      },
      {
        field: "managedObject",
        headerName: "Managed Object",
        width: 220,
        valueGetter: (params) => params.row?.managedObject || "",
      },
      {
        field: "band",
        headerName: "Band",
        width: 120,
      },
      { field: "equipmentType", headerName: "Type", width: 140 },
      {
        field: "status",
        headerName: "Status",
        width: 140,
        renderCell: (cellValues) => {
          const statusLabel = resolveDeviceStatusLabel(cellValues.row?.status);
          return <StatusIcon status={capitalizeWords(statusLabel || "Unknown")} />;
        },
      },
      { field: "ipAddress", headerName: "IP Address", width: 160 },
      { field: "model", headerName: "Model", width: 160 },
      { field: "serialNumber", headerName: "Serial Number", width: 180 },
    ],
    [deviceKey]
  );


  const getRowId = (row: IRemoteEquipment) => row.objectKey || row.id || row.serialNumber || row.equipmentIndex || "";

  return (
    <Grid container>
      <Grid item xs={12} sx={{ pb: 2 }}>
        <DasInventoryFilter data={data} />
      </Grid>
      <Grid item xs={12}>
        {isLoading && <Typography>Loading...</Typography>}
        {!isLoading && (
          <CustomDataGrid
            tableId="dasRadioNodes"
            rows={filteredData}
            columns={columns}
            getRowId={getRowId}
            disableColumnMenu={true}
            disableRowSelectionOnClick={true}
            autoHeight={true}
            paginationMode="client"
            sortingMode="client"
            onRowClick={(params) => handleRowClick(params.row)}
          />
        )}
      </Grid>
      <DasDeviceDetailsDrawer
        open={drawerOpen}
        device={selectedDevice}
        onClose={handleCloseDrawer}
      />

    </Grid>
  );
};

export default DasRadioNodes;
